import React from 'react';
import { ExternalLink, Phone, Copy, Reply, FileText, Image as ImageIcon, Video, ArrowLeft, MoreVertical, Video as VideoCall, Smile, Paperclip, Mic } from 'lucide-react';

/** Fill {{n}} in a string with sample values (falls back to a subtle chip). */
const fillVars = (text, samples = []) => {
  if (!text) return null;
  const parts = String(text).split(/(\{\{\s*\d+\s*\}\})/g);
  return parts.map((p, i) => {
    const m = p.match(/\{\{\s*(\d+)\s*\}\}/);
    if (m) {
      const n = parseInt(m[1], 10);
      const val = samples[n - 1];
      return (
        <span key={i} className={val ? 'font-medium' : 'rounded bg-emerald-500/15 px-1 text-emerald-700'}>
          {val || `{{${n}}}`}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
};

const BTN_ICON = { URL: ExternalLink, PHONE_NUMBER: Phone, COPY_CODE: Copy, QUICK_REPLY: Reply };
const HEADER_MEDIA_ICON = { IMAGE: ImageIcon, VIDEO: Video, DOCUMENT: FileText };

/**
 * Live WhatsApp preview rendered inside a mobile-phone mockup (device frame +
 * WhatsApp chat header + wallpaper + input bar) so it looks exactly like it will
 * on a real phone.
 */
export const WhatsAppTemplatePreview = ({ header, body, footer, buttons = [], samples = [], mediaFormat, contactName = 'Your customer' }) => {
  const hFmt = (header?.format || 'NONE').toUpperCase();
  const MediaIcon = HEADER_MEDIA_ICON[hFmt] || HEADER_MEDIA_ICON[mediaFormat];
  const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex justify-center" data-testid="wa-template-preview">
      {/* Phone frame */}
      <div className="w-[300px] rounded-[2.2rem] border-[10px] border-slate-900 bg-slate-900 shadow-2xl">
        <div className="overflow-hidden rounded-[1.5rem] bg-[#e5ddd5]">
          {/* Status bar */}
          <div className="flex items-center justify-between bg-[#075E54] px-3 pt-1.5 text-[9px] text-white/90">
            <span>{now}</span>
            <span className="flex items-center gap-1"><span>●●●</span><span>WiFi</span><span>100%</span></span>
          </div>
          {/* WhatsApp chat header */}
          <div className="flex items-center gap-2 bg-[#075E54] px-2 py-1.5 text-white">
            <ArrowLeft className="h-4 w-4" />
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/25 text-[11px] font-semibold">
              {(contactName || 'C').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium leading-tight">{contactName}</p>
              <p className="text-[9px] leading-tight text-white/70">online</p>
            </div>
            <VideoCall className="h-4 w-4" />
            <Phone className="h-3.5 w-3.5" />
            <MoreVertical className="h-4 w-4" />
          </div>

          {/* Chat area */}
          <div className="min-h-[230px] px-2.5 py-3" style={{ backgroundImage: 'radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px)', backgroundSize: '14px 14px' }}>
            <div className="max-w-[92%] rounded-lg rounded-tl-none bg-white px-2 py-1.5 shadow-sm">
              {/* Media header */}
              {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(hFmt) || MediaIcon ? (
                header?.previewUrl && hFmt === 'IMAGE' ? (
                  <img src={header.previewUrl} alt="header" className="mb-1.5 h-28 w-full rounded-md object-cover" data-testid="wa-preview-media-image" />
                ) : header?.previewUrl && hFmt === 'VIDEO' ? (
                  <video src={header.previewUrl} controls className="mb-1.5 h-28 w-full rounded-md bg-black object-contain" data-testid="wa-preview-media-video" />
                ) : header?.previewUrl && hFmt === 'DOCUMENT' ? (
                  <div className="mb-1.5 flex items-center gap-2 rounded-md bg-slate-100 px-2 py-3 text-slate-600" data-testid="wa-preview-media-doc">
                    <FileText className="h-6 w-6 shrink-0" /><span className="truncate text-[12px]">{header.filename || 'Document.pdf'}</span>
                  </div>
                ) : (
                  <div className="mb-1.5 flex h-24 items-center justify-center rounded-md bg-slate-100 text-slate-400" data-testid="wa-preview-media-placeholder">
                    {MediaIcon ? <MediaIcon className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
                  </div>
                )
              ) : null}
              {/* Text header */}
              {hFmt === 'TEXT' && (header?.text || '').trim() && (
                <p className="mb-1 text-[12px] font-bold leading-snug text-slate-900" data-testid="wa-preview-header">
                  {fillVars(header.text, header.sample ? [header.sample] : [])}
                </p>
              )}
              {/* Body */}
              <p className="whitespace-pre-wrap text-[12px] leading-snug text-slate-800" data-testid="wa-preview-body">
                {body?.trim() ? fillVars(body, samples) : <span className="text-slate-400">Your message body…</span>}
              </p>
              {/* Footer */}
              {(footer || '').trim() && <p className="mt-1 text-[10px] text-slate-400" data-testid="wa-preview-footer">{footer}</p>}
              <div className="mt-0.5 text-right text-[9px] text-slate-400">{now}</div>
            </div>

            {/* Buttons */}
            {(buttons || []).length > 0 && (
              <div className="mt-1 max-w-[92%] space-y-0.5" data-testid="wa-preview-buttons">
                {buttons.map((b, i) => {
                  const Icon = BTN_ICON[(b.type || '').toUpperCase()] || Reply;
                  const label = (b.type || '').toUpperCase() === 'COPY_CODE' ? `Copy code ${b.example || ''}`.trim() : (b.text || 'Button');
                  return (
                    <div key={i} className="flex items-center justify-center gap-1.5 rounded-lg bg-white px-2 py-1.5 text-[12px] font-medium text-[#00a5f4] shadow-sm">
                      <Icon className="h-3.5 w-3.5" />{label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Input bar */}
          <div className="flex items-center gap-2 bg-[#f0f0f0] px-2 py-1.5">
            <Smile className="h-4 w-4 text-slate-500" />
            <div className="flex-1 rounded-full bg-white px-2 py-1 text-[10px] text-slate-400">Message</div>
            <Paperclip className="h-4 w-4 text-slate-500" />
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#075E54] text-white"><Mic className="h-3 w-3" /></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplatePreview;
